import React from 'react';
import { ExternalLink } from 'lucide-react';

const gameCover   = '/images/games/bizenghast-cover2.png';
const mangaCover  = '/images/games/bizenghast-manga.png';
const titleArt    = '/images/games/bizenghast-title.png';
const tokyopopLogo = '/images/games/tokyopop-logo.png';

const KickstarterIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.64 4.95a3.15 3.15 0 0 0-4.36.96l-2.48 3.87-1.64-2.33a3.15 3.15 0 1 0-5.12 3.66l3.29 4.62-3.29 4.6a3.15 3.15 0 1 0 5.12 3.66l1.64-2.33 2.48 3.87a3.15 3.15 0 1 0 5.32-3.38l-3.5-5.42 3.5-5.42a3.15 3.15 0 0 0-.96-4.36z"/>
  </svg>
);

export function Bizenghast() {
  return (
    <section className="py-16 relative overflow-hidden bg-slate-900">
      {/* Neon tron grid — same pattern as rest of site */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 opacity-30" style={{
          backgroundImage: `
            linear-gradient(rgba(34,211,238,0.12) 1px, transparent 1px),
            linear-gradient(90deg, rgba(34,211,238,0.12) 1px, transparent 1px),
            linear-gradient(rgba(168,85,247,0.06) 1px, transparent 1px),
            linear-gradient(90deg, rgba(168,85,247,0.06) 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px, 60px 60px, 20px 20px, 20px 20px',
        }} />
        <div className="absolute top-1/4 left-1/3 w-96 h-96 bg-cyan-400/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/3 w-96 h-96 bg-purple-400/5 rounded-full blur-3xl" />
      </div>
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-cyan-400/40 to-transparent" />
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-cyan-400/40 to-transparent" />

      {/* Corner accents */}
      <div className="absolute top-0 left-0 w-24 h-24 pointer-events-none">
        <div className="absolute top-4 left-4 w-8 h-px bg-gradient-to-r from-cyan-400/60 to-transparent" />
        <div className="absolute top-4 left-4 w-px h-8 bg-gradient-to-b from-cyan-400/60 to-transparent" />
      </div>
      <div className="absolute bottom-0 right-0 w-24 h-24 pointer-events-none">
        <div className="absolute bottom-4 right-4 w-8 h-px bg-gradient-to-l from-cyan-400/60 to-transparent" />
        <div className="absolute bottom-4 right-4 w-px h-8 bg-gradient-to-t from-cyan-400/60 to-transparent" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Section label */}
        <div className="flex items-center justify-center space-x-3 mb-10">
          <span className="text-cyan-300 opacity-90 text-lg cyberpunk-bracket-pulse">[</span>
          <span className="text-white tracking-wider uppercase text-xl font-cyberpunk bg-gradient-to-r from-orange-300 to-cyan-300 bg-clip-text text-transparent cyberpunk-glow drop-shadow-lg">
            Kickstarter Partnership
          </span>
          <span className="text-cyan-300 opacity-90 text-lg cyberpunk-bracket-pulse">]</span>
        </div>

        {/* Main card */}
        <div className="max-w-6xl mx-auto bg-gradient-to-br from-slate-800/60 to-slate-700/30 backdrop-blur-sm rounded-2xl border border-cyan-500/20 overflow-hidden hover:border-cyan-400/40 transition-all duration-500 shadow-2xl shadow-cyan-400/5">
          <div className="h-px bg-gradient-to-r from-transparent via-cyan-400/50 to-transparent" />

          <div className="flex flex-col lg:flex-row" style={{ minHeight: '380px' }}>

            {/* Left — title art + game cover stacked, filling full height */}
            <div className="lg:w-1/2 flex flex-col">
              {/* Title art */}
              <div className="relative flex-1 overflow-hidden aspect-[16/9] lg:aspect-auto">
                <img
                  src={titleArt}
                  alt="Bizenghast The Game title art"
                  className="absolute inset-0 w-full h-full object-cover object-center"
                />
              </div>
              {/* Game cover */}
              <div className="relative flex-1 overflow-hidden border-t border-cyan-500/10 aspect-[16/9] lg:aspect-auto">
                <img
                  src={gameCover}
                  alt="Bizenghast Game Cover"
                  className="absolute inset-0 w-full h-full object-cover object-center"
                />
              </div>
            </div>

            {/* Right — info + manga cover + TokyoPop */}
            <div className="lg:w-1/2 flex flex-col border-t lg:border-t-0 lg:border-l border-cyan-500/10">

              {/* Text info */}
              <div className="p-6 md:p-8 flex-1">
                <h2 className="text-white font-black text-4xl sm:text-5xl tracking-tight leading-none mb-1">
                  BIZENGHAST
                </h2>
                <h3 className="text-cyan-300 font-cyberpunk text-sm tracking-widest uppercase mb-4">
                  The Game
                </h3>
                <p className="text-gray-300 text-sm leading-relaxed mb-4">
                  Cosmic Forces partnered with <span className="text-cyan-300 font-semibold">TokyoPop</span> on a Kickstarter campaign to bring <em>Bizenghast</em> to life as a game. The beloved American gothic manga series by{' '}
                  <span className="text-cyan-300 font-semibold">M. Alice LeGrow</span> follows a young girl haunted by the spirits of the dead in a crumbling Victorian mausoleum.
                </p>
                <div className="inline-flex items-center space-x-2 px-3 py-1.5 bg-slate-700/50 rounded border border-cyan-500/20">
                  <KickstarterIcon className="h-4 w-4 text-green-400" />
                  <span className="text-green-400 text-xs font-mono font-semibold tracking-wide">Kickstarter Campaign</span>
                </div>
              </div>

              {/* Manga cover + TokyoPop side by side */}
              <div className="flex flex-col sm:flex-row items-stretch border-t border-cyan-500/10">
                {/* TokyoPop logo + link */}
                <div className="sm:w-3/5 flex flex-col items-center justify-center gap-4 p-6 bg-slate-800/40 border-b sm:border-b-0 sm:border-r border-cyan-500/10">
                  <p className="text-gray-500 text-xs font-mono uppercase tracking-widest">In partnership with</p>
                  <img
                    src={tokyopopLogo}
                    alt="TokyoPop"
                    className="h-28 lg:h-40 w-auto"
                  />
                  <a
                    href="https://www.tokyopop.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-2 text-cyan-400 hover:text-cyan-300 transition-colors duration-300 text-sm font-mono group"
                  >
                    <span>tokyopop.com</span>
                    <ExternalLink className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-200" />
                  </a>
                </div>

                {/* Manga cover */}
                <div className="sm:w-2/5 aspect-[3/4] sm:aspect-auto bg-slate-900/80 overflow-hidden">
                  <img
                    src={mangaCover}
                    alt="Bizenghast Manga Vol. 1"
                    className="w-full h-full object-contain object-bottom"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="h-px bg-gradient-to-r from-transparent via-cyan-400/30 to-transparent" />
        </div>
      </div>
    </section>
  );
}
